import React, { useState, useRef, useEffect } from "react";
import "./clientTasksDisplay.css";
import BdrDisclosure from "../../../../components/BdrDisclosure/BdrDisclosure";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import PanelActions from "../../AdminPages/SingleClientPage/PanelActions";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { useMediaQuery, CircularProgress } from "@mui/material";
import { useLazyQuery } from "@apollo/client";
import { GET_CLIENT_COMPLETED_TASKS } from "../../../../utils/GraphQL/queries";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import TaskDisplayModal from "./TaskModal";

/*
 * Two hosts, one component. `inPanel` renders this straight into the client
 * console's VS panel: no BdrDisclosure wrapper (the panel header already names
 * the section), open from the start, and none of the document-scroll
 * behaviour — the console's page is viewport-locked and the PANEL scrolls.
 *
 * Without it, this is the collapsible row the Edit branch still uses.
 */
const ClientTasksDisplay = ({
  heightTrigger,
  incTasks,
  refetch,
  addUpdatedTaskToArr,
  clientId,
  focusRecord,
  onFocusHandled,
  inPanel,
}) => {
  const { handleSnack } = useSnackbar();
  const [barCollapsed, setBarCollapsed] = useState(!inPanel);
  const [openSingleTaskModal, setOpenSingleTaskModal] = useState(false);
  const [tasksArr, setTasksArr] = useState([]);
  // null = never fetched; [] = fetched, none exist
  const [completedTasks, setCompletedTasks] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  // Local flag for the user-initiated fetch only — the lazy query's shared
  // `loading` would also flip during background refetches and flicker the
  // button.
  const [completedBtnLoading, setCompletedBtnLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState("");
  const smaller900 = useMediaQuery("(max-width:900px)");
  const clientTasksDiv = useRef();
  // Ref, not state: refetchBothLists can be invoked from the snackbar's
  // "Restore Task?" button whose closure was frozen at mark-complete time —
  // a render-captured `completedTasks` check would read stale null there.
  const completedFetchedRef = useRef(false);
  const [getCompletedTasks] = useLazyQuery(GET_CLIENT_COMPLETED_TASKS, {
    fetchPolicy: "network-only",
  });

  // SingleClient survives /view-client/:clientId param changes (it refetches
  // instead of remounting), so completed-task state must reset per client or
  // the previous client's completed list leaks onto the new page.
  useEffect(() => {
    setCompletedTasks(null);
    setShowCompleted(false);
    completedFetchedRef.current = false;
  }, [clientId]);

  useEffect(() => {
    // incTasks is active-only from the server (activeTasksOnly), but a task
    // just marked complete can sit in it as active=false until the parent
    // refetch lands — filter, don't trust.
    const activeSort = (incTasks || [])
      ?.filter((task) => task?.active === true)
      ?.toSorted((a, b) => {
        return a?.dueDate > b?.dueDate ? 1 : -1;
      });

    setTasksArr(activeSort);
  }, [incTasks]);

  // Anything due before today. Built by (year, month, day) construction rather
  // than ms arithmetic so a DST boundary inside the range cannot shift the
  // cutoff off its intended day.
  const startOfToday = (() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  })();

  const overdueTasks =
    tasksArr?.filter((task) => {
      const due = Number(task?.dueDate);
      return Number.isFinite(due) && due > 0 && due < startOfToday;
    })?.length || 0;

  const fetchCompletedTasks = async () => {
    if (!clientId) {
      return false;
    }

    try {
      const { data } = await getCompletedTasks({
        variables: { clientId },
      });

      if (data?.getClientCompletedTasks) {
        const completedSort = [...data.getClientCompletedTasks]?.toSorted(
          (a, b) => {
            return a?.dueDate > b?.dueDate ? 1 : -1;
          }
        );
        setCompletedTasks(completedSort);
        completedFetchedRef.current = true;
        return true;
      }
    } catch (err) {
      // fall through — caller decides how to surface the failure
    }
    return false;
  };

  const toggleCompleted = async () => {
    if (showCompleted) {
      setShowCompleted(false);
      if (heightTrigger) {
        heightTrigger();
      }
      return;
    }

    if (completedTasks === null) {
      setCompletedBtnLoading(true);
      const fetched = await fetchCompletedTasks();
      setCompletedBtnLoading(false);
      if (!fetched) {
        handleSnack("Couldn't load completed tasks — please try again", 3000);
        return;
      }
    }
    setShowCompleted(true);
    if (heightTrigger) {
      heightTrigger();
    }
  };

  // TaskModal refetch wrapper — a mark complete / mark incomplete flips a
  // task between the two lists, so refresh the completed list too if it has
  // been fetched (ref check — see completedFetchedRef note above).
  const refetchBothLists = () => {
    if (refetch) {
      refetch();
    }
    if (completedFetchedRef.current) {
      fetchCompletedTasks();
    }
  };

  // TaskModal update wrapper — parent merge only covers the active array, so
  // mirror edits (note, due date, assignee) into the completed list when the
  // task lives there. Functional update + re-sort: no stale-closure gate, and
  // a due-date edit keeps the list ordered like a fresh fetch.
  const handleUpdatedTask = (taskUpdated) => {
    if (addUpdatedTaskToArr) {
      addUpdatedTaskToArr(taskUpdated);
    }

    if (taskUpdated?.id) {
      setCompletedTasks((prev) =>
        prev === null
          ? prev
          : prev
              ?.map((task) =>
                task?.id === taskUpdated?.id ? taskUpdated : task
              )
              ?.toSorted((a, b) => {
                return a?.dueDate > b?.dueDate ? 1 : -1;
              })
      );
    }
  };

  const openBarCollapse = () => {
    if (heightTrigger) {
      heightTrigger();
    }

    // The console's page does not scroll; its panel does.
    if (inPanel) return;

    setTimeout(() => {
      if (smaller900 === true && barCollapsed === true) {
        const navHeight = document.querySelector(
          "#admin-nav-height-marker"
        )?.offsetHeight;
        const pagePos = clientTasksDiv.current.offsetTop;

        const totalMobileScroll = pagePos - navHeight - 5;
        window.scrollTo(0, totalMobileScroll);
      }

      if (smaller900 === false && barCollapsed === true) {
        clientTasksDiv.current.scrollIntoView({ block: "start" });
      }
    }, 500);

    setBarCollapsed((prev) => !prev);
  };

  const handleTaskModal = (bool) => {
    setOpenSingleTaskModal(bool);
  };

  const taskRow = (task, index, keyPrefix) => {
    let bulletColor = task?.active ? "green" : "red";
    const pastDue = parseInt(task?.dueDate) < Date.now();
    const dueClass = task?.active
      ? pastDue
        ? "client-task-due-color-red"
        : "client-task-due-color-green"
      : "client-task-due-color-white";

    return (
      <li key={`${keyPrefix}-${task?.id}-${index}`}>
        <button
          className={
            task?.active
              ? "client-tasks-item-box bdr-border-thin"
              : "client-tasks-item-box bdr-border-thin client-tasks-item-inactive"
          }
          onClick={() => {
            setSelectedTask(task?.id);
            handleTaskModal(true);
          }}
        >
          <span className="client-tasks-item-box-item-1">
            <span
              className={`client-tasks-circle client-task-bullet-color-${bulletColor}`}
            />
            {/* The assignee was running into the task type as one string -
                "F/U - All" reads as a title, not as "assigned to All". */}
            <span className="client-task-type">{task?.taskType}</span>
            {task?.assigneeName && (
              <span className="client-task-assignee">
                {task?.assigneeName}
              </span>
            )}
          </span>
          {/* The note was the row's dead middle. Truncated by CSS rather than
              by slicing, so the full text is still in the title on hover. */}
          {task?.note && (
            <span className="client-task-note" title={task?.note}>
              {task?.note}
            </span>
          )}
          <span className="client-task-due">
            <span className={dueClass}>
              {task?.active && pastDue ? "Overdue: " : "Due: "}
            </span>
            <span>{BAGroupTimeFormat(task?.dueDate, false, true, true)}</span>
          </span>
        </button>
      </li>
    );
  };

  /*
   * An Activity row was clicked for a record this section owns. The Activity
   * panel cannot open these modals itself — they live in here, with the state
   * they depend on — so it switches to this section and hands over the id.
   * The nonce makes the same row clickable twice: a plain id would be an
   * unchanged value and the effect would not re-run after the modal is closed.
   */
  useEffect(() => {
    if (!focusRecord?.id) return;
    setSelectedTask(String(focusRecord.id));
    /* Consumed. Unconditional here because this one needs no lookup - the id
       alone is enough to act on. */
    if (onFocusHandled) onFocusHandled();
    setOpenSingleTaskModal(true);
  }, [focusRecord, onFocusHandled]);

  const taskModal = (
      <TaskDisplayModal
        openModal={openSingleTaskModal}
        setOpenModal={handleTaskModal}
        handleUpdatedTasksArr={handleUpdatedTask}
        taskId={selectedTask}
        refetch={refetchBothLists}
      />
  );

  const listBody = (
    <>
        {tasksArr?.length > 0 ? (
          <div className="client-tasks-info-display">
            <ul>
              {tasksArr?.map((task, index) =>
                taskRow(task, index, "client-task-item")
              )}
            </ul>
          </div>
        ) : (
          <div className="client-tasks-none-display bdr-border-thin">No Tasks to Display</div>
        )}
      {/* In a panel this portals into the header's right-hand side; on the
          Edit branch it renders here exactly as before. */}
      <PanelActions active={inPanel}>
        <BdrButton
          size="sm"
          variant="action"
          onClick={toggleCompleted}
          disabled={completedBtnLoading}
          ariaLabel={
            completedBtnLoading ? "Loading completed tasks" : undefined
          }
        >
          {completedBtnLoading ? (
            <CircularProgress color="inherit" size={12} />
          ) : showCompleted ? (
            "Hide Completed"
          ) : (
            "View Completed"
          )}
        </BdrButton>
      </PanelActions>
        {showCompleted && completedTasks !== null && (
          <>
            <div className="client-tasks-completed-divider">Completed</div>
            {completedTasks?.length > 0 ? (
              <div className="client-tasks-info-display">
                <ul>
                  {completedTasks?.map((task, index) =>
                    taskRow(task, index, "client-task-completed-item")
                  )}
                </ul>
              </div>
            ) : (
              <div className="client-tasks-none-display bdr-border-thin">
                No Completed Tasks
              </div>
            )}
        </>
      )}
    </>
  );

  if (inPanel) {
    return (
      <div className="scc-list" ref={clientTasksDiv}>
        {taskModal}
        {listBody}
      </div>
    );
  }

  return (
    <div
      id="client-tasks-height-marker"
      className="client-tasks-container"
      ref={clientTasksDiv}
    >
      {taskModal}
      <BdrDisclosure
        open={!barCollapsed}
        onToggle={openBarCollapse}
        label="Tasks"
        icon={<TaskAltIcon />}
        count={tasksArr?.length}
        status={
          overdueTasks > 0
            ? { tone: "danger", text: `${overdueTasks} overdue` }
            : undefined
        }
        emptyText="No open tasks"
        bodyScroll="cap"
      >
        {listBody}
      </BdrDisclosure>
    </div>
  );
};

export default ClientTasksDisplay;
